import { useSession } from "@internal/auth";
import { useEffect, useState } from "react";
import {
  BODY_MEASUREMENT_TYPE,
  type BodyMeasurementEntryData,
  type BodyMeasurementTypeKey,
  bodyMeasurementId,
} from "@/lib/body-measurements";
import { getDbForUser, type PlannerDoc } from "@/lib/db";

function docToEntry(doc: PlannerDoc): BodyMeasurementEntryData {
  const data = (doc.data ?? {}) as Partial<BodyMeasurementEntryData>;
  return {
    date: typeof data.date === "string" ? data.date : "",
    values: data.values ?? {},
  };
}

export function useBodyMeasurementEntry(date: string | null): {
  entry: BodyMeasurementEntryData | null;
  loading: boolean;
} {
  const { user } = useSession();
  const [state, setState] = useState<{
    entry: BodyMeasurementEntryData | null;
    loading: boolean;
  }>({ entry: null, loading: true });

  useEffect(() => {
    if (!user || !date) {
      setState({ entry: null, loading: false });
      return;
    }
    let cancelled = false;
    let sub: { unsubscribe: () => void } | null = null;
    (async () => {
      const db = await getDbForUser(user.id);
      if (cancelled) return;
      sub = db.daily_planner
        .findOne(bodyMeasurementId(user.id, date))
        .$.subscribe((doc) => {
          setState({
            entry: doc ? docToEntry(doc.toJSON() as PlannerDoc) : null,
            loading: false,
          });
        });
    })();
    return () => {
      cancelled = true;
      sub?.unsubscribe();
    };
  }, [user, date]);

  return state;
}

/**
 * All entries with startDate ≤ date ≤ endDate, sorted ascending by date.
 * Queries by the indexed (user_id, type) pair and filters the date range
 * in memory — the per-user collection is small.
 */
export function useBodyMeasurementEntries(
  startDate: string | null,
  endDate: string | null,
): { entries: BodyMeasurementEntryData[]; loading: boolean } {
  const { user } = useSession();
  const [state, setState] = useState<{
    entries: BodyMeasurementEntryData[];
    loading: boolean;
  }>({ entries: [], loading: true });

  useEffect(() => {
    if (!user || !startDate || !endDate) {
      setState({ entries: [], loading: false });
      return;
    }
    let cancelled = false;
    let sub: { unsubscribe: () => void } | null = null;
    (async () => {
      const db = await getDbForUser(user.id);
      if (cancelled) return;
      sub = db.daily_planner
        .find({
          selector: { user_id: user.id, type: BODY_MEASUREMENT_TYPE },
        })
        .$.subscribe((docs) => {
          const entries = docs
            .map((d) => docToEntry(d.toJSON() as PlannerDoc))
            .filter((e) => e.date >= startDate && e.date <= endDate)
            .sort((a, b) => (a.date < b.date ? -1 : 1));
          setState({ entries, loading: false });
        });
    })();
    return () => {
      cancelled = true;
      sub?.unsubscribe();
    };
  }, [user, startDate, endDate]);

  return state;
}

export type LatestValueByType = Partial<
  Record<BodyMeasurementTypeKey, { value: number; date: string }>
>;

/** Most recent recorded value per type on dates strictly before `date`. */
export function useLatestBodyMeasurementBefore(date: string | null): {
  latestByType: LatestValueByType;
  loading: boolean;
} {
  const { user } = useSession();
  const [state, setState] = useState<{
    latestByType: LatestValueByType;
    loading: boolean;
  }>({ latestByType: {}, loading: true });

  useEffect(() => {
    if (!user || !date) {
      setState({ latestByType: {}, loading: false });
      return;
    }
    let cancelled = false;
    let sub: { unsubscribe: () => void } | null = null;
    (async () => {
      const db = await getDbForUser(user.id);
      if (cancelled) return;
      sub = db.daily_planner
        .find({
          selector: { user_id: user.id, type: BODY_MEASUREMENT_TYPE },
        })
        .$.subscribe((docs) => {
          const before = docs
            .map((d) => docToEntry(d.toJSON() as PlannerDoc))
            .filter((e) => e.date < date)
            .sort((a, b) => (a.date < b.date ? -1 : 1));
          const latestByType: LatestValueByType = {};
          for (const entry of before) {
            for (const [key, value] of Object.entries(entry.values)) {
              if (typeof value !== "number") continue;
              latestByType[key as BodyMeasurementTypeKey] = {
                value,
                date: entry.date,
              };
            }
          }
          setState({ latestByType, loading: false });
        });
    })();
    return () => {
      cancelled = true;
      sub?.unsubscribe();
    };
  }, [user, date]);

  return state;
}
